import { Editor, JSONContent } from '@tiptap/core';
import { Mark } from '@tiptap/pm/model';
import { AllSelection } from '@tiptap/pm/state';
import { last, first } from 'lodash';

export type ProsemirrorTypeNames = 'textStyle';
/**
 * Get all the text nodes that are part of the current selection and their marks
 */
export function allMarksForSelectionTextNodes(editor: Editor | null): (readonly Mark[])[] {
  if (!editor || !editor.state.selection) return [];
  const { from, to } = editor.state.selection;

  const marks: (readonly Mark[])[] = [];
  editor.view.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'text') {
      marks.push(node.marks);
    }
  });
  return marks;
}

/**
 * Get all marks of a certain type that are part of the current selection
 */
export function allMarksInSelectionForTypeName(
  editor: Editor | null,
  typeName: ProsemirrorTypeNames
): (Mark | undefined)[] {
  const nodeMarks = allMarksForSelectionTextNodes(editor);
  return nodeMarks.map((marks) => marks.find((mark) => mark.type.name === typeName));
}

/**
 * Are we selecting from left to right or right to left
 */
export function selectionDirection(editor: Editor | null): 'forward' | 'backward' {
  if (!editor) return 'forward';
  const { $anchor, $head } = editor.state.selection;
  return $anchor.pos < $head.pos ? 'forward' : 'backward';
}

export function hasSelection(editor: Editor | null) {
  return editor?.state.selection.from !== editor?.state.selection.to;
}

/**
   Gets the mark that is currently under the mouse cursor when selecting text.
   This is needed because `editor.getAttributes` does not return undefined if there is no mark
   for the current node in the selection, but the last known mark value.
  */
export function markUnderCursorForType(editor: Editor | null, typeName: ProsemirrorTypeNames): Mark | undefined {
  const marks = allMarksInSelectionForTypeName(editor, typeName);
  const direction = selectionDirection(editor);
  if (direction === 'forward') {
    return last(marks);
  } else {
    return first(marks);
  }
}

/**
 * Decide if the update should target the whole component or just the current focused element(s)
 */
export const editorUpdateShouldTargetChild = (editor: Editor | null, isDetailMode: boolean | undefined): boolean => {
  if (!editor) return false;
  if (isEmpty(editor) || isSelectAll(editor)) return false;
  if (hasSelection(editor)) return true;
  return !!isDetailMode;
};

/**
 * Detect if we selected all the text in the editor
 * Because we can than treat it as a component level update
 */
const isSelectAll = (editor: Editor): boolean => {
  if (editor.state.selection instanceof AllSelection) return true;
  const state = editor.state;
  const totalLength = state.doc.content.size;
  const selection = state.selection;
  if (selection && totalLength) {
    const { from, to } = selection;
    const text = state.doc.textBetween(from, to, blockSeparator);
    const allText = editor.getText({ blockSeparator });
    return text === allText;
  }
  return false;
};
// if you want to be able to compare, use the same blockSeparator
const blockSeparator = ' ';

/**
 * Replaces the selected text in the editor with new content.
 * Handles both full editor selection and partial selections.
 *
 * @param editor - The TipTap editor instance
 * @param content - The JSON content to insert
 */
export const replaceSelectedText = (content: JSONContent, editor: Editor | null) => {
  if (!editor) return;

  if (isSelectAll(editor)) {
    editor.commands.setContent(content);
    return;
  }

  // If content is a single paragraph, extract its content while preserving formatting
  if (content.content && content.content.length === 1 && content.content[0].type === 'paragraph') {
    const paragraphContent = content.content[0].content;
    if (paragraphContent) {
      editor.commands.insertContent(paragraphContent);
      return;
    }
  }

  editor.commands.insertContent(content);
};

const isEmpty = (editor: Editor): boolean => editor.isEmpty;
